import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RiArrowLeftLine, RiShoppingCart2Line, RiShoppingCartLine } from 'react-icons/ri'
import { useProject } from '@/api/useProjects'
import { useMaterials } from '@/api/useMaterials'
import { usePrintSizes } from '@/api/usePrintSizes'
import { usePrices } from '@/api/usePrices'
import { calculateTierPrice } from '@/lib/pricing'
import { PriceCalculator } from '@/components/orders/PriceCalculator/PriceCalculator'
import { PageSelector } from '@/components/orders/PageSelector'
import { Button, Spinner } from '@/components/ui'
import { useCartStore, selectItemCount } from '@/stores/useCartStore'
import { ROUTES, buildRoute } from '@/constants/routes'
import styles from './OrderCheckout.module.css'

export function OrderCheckout() {
    const { projectId } = useParams<{ projectId: string }>()
    const navigate = useNavigate()
    const id = projectId || ''

    const { data: project, isLoading: loadingProject } = useProject(id)
    const { data: materials } = useMaterials()
    const { data: sizes } = usePrintSizes()

    const addItem = useCartStore((s) => s.addItem)
    const cartCount = useCartStore(selectItemCount)

    const [selectedMaterial, setSelectedMaterial] = useState<number | null>(null)
    const [selectedSize, setSelectedSize] = useState<number | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [frontPageId, setFrontPageId] = useState<number | null>(null)
    const [backPageId, setBackPageId] = useState<number | null>(null)

    useEffect(() => {
        if (project?.pages && project.pages.length > 0) {
            if (!frontPageId) setFrontPageId(project.pages[0].id)
            if (!backPageId && project.pages.length >= 2) setBackPageId(project.pages[1].id)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project])

    // Fetch all prices for the selected material (to know which sizes are available)
    const { data: materialPrices } = usePrices(selectedMaterial || undefined, undefined)
    // Fetch exact price for selected material+size combo
    const { data: prices } = usePrices(selectedMaterial || undefined, selectedSize || undefined)

    const priceConfig = prices?.[0]
    const basePricePerMillar = priceConfig ? parseFloat(priceConfig.price_per_millar) : 0
    const tierResult = priceConfig
        ? calculateTierPrice(basePricePerMillar, quantity, priceConfig.tiers ?? [])
        : null
    const pricePerMillar = tierResult?.effectivePrice ?? basePricePerMillar

    // Only show sizes that have a price config for the selected material
    const availableSizeIds = new Set(materialPrices?.map((p) => p.size) ?? [])
    const filteredSizes = selectedMaterial
        ? sizes?.filter((s) => availableSizeIds.has(s.id))
        : sizes

    const selectedSizeObj = sizes?.find((s) => s.id === selectedSize)

    // Clear size selection when material changes and current size isn't available
    useEffect(() => {
        if (selectedMaterial && selectedSize && !availableSizeIds.has(selectedSize)) {
            setSelectedSize(null)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMaterial, materialPrices])

    const buildCartItem = () => {
        if (!frontPageId || !selectedMaterial || !selectedSize) {
            toast.error('Selecciona material, tamaño y asegúrate de tener una página')
            return null
        }
        if (!pricePerMillar) {
            toast.error('No hay precio disponible para esta combinación')
            return null
        }

        const materialObj = materials?.find((m) => m.id === selectedMaterial)
        const sizeObj = sizes?.find((s) => s.id === selectedSize)

        return {
            projectId: id,
            projectName: project?.name || 'Sin nombre',
            frontPageId,
            frontPageThumbnail: undefined,
            backPageId: backPageId || null,
            backPageThumbnail: undefined,
            materialId: selectedMaterial,
            materialName: materialObj?.name || '',
            sizeId: selectedSize,
            sizeName: sizeObj?.name || '',
            quantityMillares: quantity,
            pricePerMillar,
        }
    }

    const handleAddToCart = () => {
        const item = buildCartItem()
        if (!item) return
        addItem(item)
        toast.success(
            (t) => (
                <span>
                    Producto agregado al carrito{' '}
                    <button
                        style={{ fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                        onClick={() => { toast.dismiss(t.id); navigate(ROUTES.cart) }}
                    >
                        Ver carrito
                    </button>
                </span>
            ),
            { duration: 4000 }
        )
    }

    if (loadingProject) {
        return <div className={styles.loading}><Spinner size="lg" /></div>
    }

    return (
        <div className={styles.container}>
            <div className={styles.topBar}>
                <button className={styles.backButton} onClick={() => navigate(buildRoute.editor(id))}>
                    <RiArrowLeftLine /> Volver al editor
                </button>

                {cartCount > 0 && (
                    <button
                        className={styles.cartIndicator}
                        onClick={() => navigate(ROUTES.cart)}
                    >
                        <RiShoppingCartLine />
                        <span>{cartCount} en carrito</span>
                    </button>
                )}
            </div>

            <h1 className={styles.title}>Configurar impresión: {project?.name}</h1>

            <div className={styles.layout}>
                <div className={styles.form}>
                    {/* Page Selection */}
                    <div className={styles.section}>
                        <h3>Páginas</h3>
                        {project && project.pages.length === 1 ? (
                            <p className={styles.singlePageNote}>
                                Se usará la única página del proyecto como anverso.
                            </p>
                        ) : project ? (
                            <div className={styles.pageSelectors}>
                                <PageSelector
                                    label="Anverso (frente) *"
                                    pages={project.pages}
                                    selectedId={frontPageId}
                                    onSelect={(pageId) => { if (pageId !== null) setFrontPageId(pageId) }}
                                />
                                <PageSelector
                                    label="Reverso (opcional)"
                                    pages={project.pages}
                                    selectedId={backPageId}
                                    onSelect={setBackPageId}
                                    allowNone
                                    noneLabel="Sin reverso"
                                />
                            </div>
                        ) : null}
                    </div>

                    {/* Material */}
                    <div className={styles.section}>
                        <h3>Material *</h3>
                        <div className={styles.optionGrid}>
                            {materials?.map((m) => (
                                <button
                                    key={m.id}
                                    className={`${styles.optionCard} ${selectedMaterial === m.id ? styles.selected : ''}`}
                                    onClick={() => setSelectedMaterial(m.id)}
                                >
                                    {m.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Size */}
                    <div className={styles.section}>
                        <h3>Tamaño *</h3>
                        {selectedMaterial && filteredSizes?.length === 0 && (
                            <p className={styles.minNote}>No hay tamaños disponibles para este material.</p>
                        )}
                        <div className={styles.optionGrid}>
                            {filteredSizes?.map((s) => (
                                <button
                                    key={s.id}
                                    className={`${styles.optionCard} ${selectedSize === s.id ? styles.selected : ''}`}
                                    onClick={() => setSelectedSize(s.id)}
                                >
                                    {s.name}
                                    <span className={styles.sizeDetail}>{s.width_mm}x{s.height_mm}mm</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Quantity */}
                    <div className={styles.section}>
                        <h3>Cantidad (millares) *</h3>
                        <div className={styles.quantityRow}>
                            <button
                                className={styles.qtyBtn}
                                onClick={() => {
                                    const step = priceConfig?.allow_half_millar ? 0.5 : 1
                                    const minQty = priceConfig?.min_millares ?? 1
                                    setQuantity(Math.max(minQty, quantity - step))
                                }}
                                disabled={quantity <= (priceConfig?.min_millares ?? 1)}
                            >
                                −
                            </button>
                            <input
                                type="number"
                                min={priceConfig?.allow_half_millar ? 0.5 : (priceConfig?.min_millares || 1)}
                                step={priceConfig?.allow_half_millar ? 0.5 : 1}
                                value={quantity}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value)
                                    if (!isNaN(val) && val > 0) setQuantity(val)
                                }}
                                className={styles.numberInput}
                            />
                            <button
                                className={styles.qtyBtn}
                                onClick={() => {
                                    const step = priceConfig?.allow_half_millar ? 0.5 : 1
                                    setQuantity(quantity + step)
                                }}
                            >
                                +
                            </button>
                        </div>
                        {priceConfig?.allow_half_millar && (
                            <p className={styles.minNote}>0.5 millar = 500 unidades</p>
                        )}
                        {priceConfig?.min_millares != null && priceConfig.min_millares !== 1 && (
                            <p className={styles.minNote}>Mínimo: {priceConfig.min_millares} millar(es)</p>
                        )}
                        {tierResult?.discount && tierResult.discount > 0 && (
                            <p className={styles.discountNote}>
                                {tierResult.discount.toFixed(0)}% de descuento por volumen aplicado
                            </p>
                        )}
                        {tierResult?.nextTier && (
                            <p className={styles.tierHint}>
                                Comprando {tierResult.nextTier.minMillares}+ millares pagas S/ {tierResult.nextTier.price.toFixed(2)}/millar
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.summary}>
                    <PriceCalculator
                        pricePerMillar={pricePerMillar}
                        basePricePerMillar={basePricePerMillar}
                        quantity={quantity}
                        shippingCost={0}
                        discount={tierResult?.discount ?? 0}
                        documentWidth={project?.width_mm}
                        documentHeight={project?.height_mm}
                        printWidth={selectedSizeObj?.width_mm}
                        printHeight={selectedSizeObj?.height_mm}
                    />

                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            onClick={handleAddToCart}
                            className={styles.actionButton}
                        >
                            <RiShoppingCart2Line />
                            Agregar al carrito
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
